import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import copy from "../../images/copy.svg";
import copy2 from "../../images/copy2.svg";
import like from "../../images/like.svg";
import TipsterFunction from "./tipsterFunction";
import moment from "moment";
import {
  useGetTipsters,
  useGetBookingCodes,
  useSupportedBookies,
  useUserPublicCodes,
  useGetCurrentUser,
} from "../../api/queries";
import { useRecoilState, useRecoilValue } from "recoil";
import { auth_user, selectAuthUser } from "../../atom";
import { Button, useToast } from "../../lib";
import {
  useLikeBookingCode,
  useUnLikeBookingCode,
  useCopyBookinCode,
  useLogin,
} from "../../api/mutations";
import Functions from "./functions";
import { IoCloseCircleOutline } from "react-icons/io5";
import { useParams, useLocation, NavLink } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { LoginSchema } from "../../validation/schema";
import { saveToken } from "../../lib/utils";
import AuthContext from "../../context/AuthContext";
import Countdown from "react-countdown";
import Pagination from "./pagination";

const Tipster = ({ isPublic = false }) => {
  const { mutateAsync: likeBookinCode } = useLikeBookingCode();
  const { mutateAsync: unlikeBookinCode } = useUnLikeBookingCode();
  const { mutateAsync: copyBookingCode } = useCopyBookinCode();
  const { data: bookies } = useSupportedBookies("ng");
  const toast = useToast();
  const { setAuth } = useContext(AuthContext);
  const [user, setAuthUser] = useRecoilState(auth_user);
  const { mutateAsync, isPending } = useLogin();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const [filter, setFilter] = useState({
    bookies: [] // Initialize as empty array
  });
  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams.entries());
  const [codes, setCodes] = useState([]);
  const ref = useRef(true);
  const { refetch: reload } = useGetCurrentUser();
  const [openModal, setOpenModal] = useState(false);
  const isSignedIn = Boolean(Object.keys(user || {}).length);
  const { data, isLoading, refetch, error } = useGetBookingCodes(
    {
      ...filter,
      userExternalId: user.externalId,
    },
    isSignedIn
  );
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const { data: user_public_codes, refetch: reload_user_public_code } =
    useUserPublicCodes(
      {
        userExternalId: user.externalId || params?.externalId,
      },
      !isSignedIn
    );

  const handleCopyCode = useCallback(async (code, bookingCodeExternalId) => {
    try {
      await copyBookingCode({
        bookingCodeExternalId,
      });
      refetch();
      navigator.clipboard.writeText(code).then(() => {
        toast({
          type: "success",
          message: "Code Copied",
        });
      });
    } catch (e) {
      console.log(e, "error");
    }
  }, []);

  const handleLike = useCallback(async (bookingCodeExternalId) => {
    try {
      await likeBookinCode({
        bookingCodeExternalId,
      });
      refetch();
    } catch (e) {
      if (
        e?.response?.data?.message ===
        "User has already liked this booking code"
      ) {
        await unlikeBookinCode({
          bookingCodeExternalId,
        });
        refetch();
      }
    }
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const applyFilter = useCallback(async () => {
    try {
      const response = await refetch();
      setCodes(response?.data?.data || []);
      toggleMenu();
    } catch (error) {
      console.error("Error applying filters:", error);
      toast({
        type: "error",
        message: "Failed to apply filters",
      });
    }
  }, [refetch, toggleMenu]); // ← Only these dependencies

  useEffect(() => {
    if (ref.current) {
      (async () => {
        if (isPublic && params?.externalId) {
          const response = await reload_user_public_code();
          setCodes(response?.data?.data || []);
        } else {
          const response = await refetch();
          setCodes(response?.data?.data || []);
        }
      })();
      ref.current = false;
    }
  }, [params, ref, setCodes, isPublic]);

  const handleSubmit = async (values) => {
    try {
      const response = await mutateAsync(values);
      const accessToken = response?.accessToken;
      const refreshToken = response?.refreshToken;
      saveToken("accessToken", accessToken);
      saveToken("refreshToken", refreshToken);

      const { data } = await reload();
      setAuthUser(data);
      setAuth({ accessToken, refreshToken });
      toast({
        type: "success",
        message: "Authenticated",
      });
      setOpenModal(false);
    } catch (error) {
      console.log(error, "error");
      toast({
        type: "error",
        message: error?.response?.data?.message ?? "An Error occured",
      });
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleBetCodes = Array.isArray(codes)
    ? codes.slice(startIndex, endIndex)
    : [];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
      return <p>Expired</p>;
    } else {
      return (
        <>
          <div className="flex text-[#7e00cc] justify-start items-center gap-1">
            {[
              {
                value: days,
                title: "Days",
              },
              { value: hours, title: "Hours" },
              {
                value: minutes,
                title: "Minutes",
              },
              { value: seconds, title: "Seconds" },
            ].map((entry, idx) => (
              <p key={entry.title} className="font-tomato-regular text-sm ">
                {entry.value}
                {entry.title !== "Seconds" ? " :" : ""}
              </p>
            ))}
          </div>
        </>
      );
    }
  };
  return (
    <div className="bac-table ">
      {openModal && (
        <div
          className="w-full z-50 h-screen fixed flex justify-center items-center top-0"
          onClick={() => setOpenModal(false)}
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="md:h-[500px] rounded-sm w-[95%] h-[70%]  lg:p-20 p-6 lg:w-[50%] bg-white"
          >
            <h3 className="header-signup lg:text-[30px] text-[20px] md:text-[40px]">
              Oh! You need to login to use
              <br /> Cuppycode for FREE
            </h3>
            <div className="flex justify-between items-center gap-3">
              <Formik
                initialValues={{ username: "", password: "" }}
                validationSchema={LoginSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="row padding-form ">
                    <div className="col-md-6 col-12">
                      <Field
                        className="input-z"
                        type="text"
                        name="username"
                        placeholder="Enter Username e.g Swiftkid"
                      />
                      <ErrorMessage
                        className="form-field-error"
                        name="username"
                        component="div"
                      />
                    </div>
                    <div className="col-md-6 col-12">
                      <Field
                        className="input-z"
                        type="password"
                        name="password"
                        placeholder="Enter Password"
                      />
                      <ErrorMessage
                        className="form-field-error"
                        name="password"
                        component="div"
                      />
                    </div>
                    <div className="flex lg:flex-row flex-col lg:justify-between lg:items-center mt-10">
                      <p className="forgot">
                        Don't have an account?{" "}
                        <span className="terms">
                          {" "}
                          <NavLink to="/signup" className="login">
                            {" "}
                            Register{" "}
                          </NavLink>
                        </span>
                      </p>

                      <div className="flex justify-center lg:justify-end">
                        <Button loading={isPending || isLoading}>Login</Button>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      )}
      <div className="container">
        <div className="row">
          <div className="col-xxl">
            {!isPublic && (
              <TipsterFunction onClick={toggleMenu}></TipsterFunction>
            )}
            {isOpen && (
              <div
                style={{ fontFamily: "Outfit" }}
                className="fixed top-0 w-full left-0 h-screen bg-[rgba(0,0,0,0.5)] z-50"
              >
                <div className="flex justify-between w-full text-black h-full">
                  <div className="h-full w-full lg:w-1/4 bg-white transition-all ease-in-out relative p-4 pb-10 overflow-x-scroll">
                    <div
                      onClick={() => toggleMenu()}
                      className="absolute right-0 top-0 m-4 cursor-pointer"
                    >
                      <IoCloseCircleOutline size={25} />
                    </div>
                    <h2 className="font-semibold text-lg pt-16">
                      Filter bookings code by
                    </h2>
                    <div className="pt-4 ">
                      <h4 className="font-regular text-sm text-[rgba(0,0,0,0.5)]">
                        Odd
                      </h4>
                      <input
                        className="input-z"
                        type="number"
                        name="username"
                        placeholder="Enter Minimum Odds value"
                        onChange={(event) =>
                          setFilter((prev) => ({
                            ...prev,
                            minOdds: event.target.value,
                          }))
                        }
                      />
                      <input
                        className="input-z"
                        type="number"
                        name="username"
                        placeholder="Enter Maximum Odds value"
                        onChange={(event) =>
                          setFilter((prev) => ({
                            ...prev,
                            maxOdds: event.target.value,
                          }))
                        }
                      />
                      <h4 className="font-regular pt-4 text-sm text-[rgba(0,0,0,0.5)]">
                        Booking platform
                      </h4>
                      <div className="w-full pt-2 flex flex-wrap justify-start items-center gap-2">
                        {Array.isArray(bookies)
                          ? bookies.map((element) => (
                            <div
                              className="flex justify-start items-start gap-0 font-regular text-sm text-black"
                              key={element?.brand}
                            >
                              <input
                                value={element.bookie}
                                type="checkbox"
                                id={element.brand}
                                onChange={(event) => {
                                  const selected_bookies = [
                                    ...(filter?.bookies || []),
                                  ];
                                  if (
                                    selected_bookies.includes(element.bookie)
                                  ) {
                                    selected_bookies.splice(
                                      selected_bookies.indexOf(
                                        element.bookie
                                      ),
                                      1
                                    );
                                  } else {
                                    selected_bookies.push(element.bookie);
                                  }
                                  setFilter((prev) => ({
                                    ...prev,
                                    bookies: selected_bookies.join(','),
                                  }));
                                }}
                              />
                              <label
                                className="-mt-1"
                                htmlFor={element.brand}
                              >
                                {element?.brand}
                              </label>
                            </div >
                          ))
                          : null}
                      </div >
                      <div className="w-full h-[100px]">
                        {/* <Button onClick={toggleMenu}>Apply filter</Button> */}
                        <Button onClick={applyFilter}>Apply filter</Button>
                      </div>
                    </div >
                  </div >
                  <div
                    onClick={toggleMenu}
                    className="h-full lg:grow grow-0 "
                  />
                </div >
              </div >
            )}

            <div className="table-bacground">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Username</th>
                      <th scope="col">Platform</th>
                      <th scope="col">Booking code</th>
                      <th scope="col">Total Odds</th>
                      <th scope="col"></th>
                      <th scope="col"></th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(visibleBetCodes)
                      ? visibleBetCodes.map((entry) => (
                        <tr key={entry.externalId}>
                          <td>@{entry.user?.username}</td>
                          <td>{entry?.bookieName}</td>
                          <td className={`flex justify-start min-w-[130px] items-center ${isPublic &&
                            "blur-sm"
                            }`}>
                            {entry?.code || "V4JVL2"}{" "}
                            <img
                              src={copy2}
                              onClick={() => {
                                if (Object.keys(user).length && !isPublic) {
                                  handleCopyCode(
                                    entry?.code,
                                    entry?.externalId
                                  );
                                } else {
                                  setOpenModal(true);
                                }
                              }}
                              className="img-fluid copy2 cursor-pointer"
                              alt="cop2"
                            />
                          </td>

                          <td className="min-w-[50px]">{entry?.odds}</td>
                          <td>
                            <button
                              onClick={() => {
                                if (Object.keys(user).length) {
                                  handleCopyCode(
                                    entry?.code,
                                    entry?.externalId
                                  );
                                } else {
                                  setOpenModal(true);
                                }
                              }}
                              className="copy-btn flex justify-center items-center"
                            >
                              <img
                                src={copy}
                                className="img-fluid copy"
                                alt="icon"
                              />
                              copy
                            </button>
                          </td>
                          <td className="flex justify-center min-w-[90px] items-center">
                            <tags className="circle-tag">
                              {entry?._count?.likedBy}
                            </tags>
                            <img
                              src={like}
                              onClick={() => handleLike(entry?.externalId)}
                              className="img-fluid like cursor-pointer"
                              alt="like"
                            />
                          </td>
                          <td className="capitalize min-w-[130px]">
                            <Countdown
                              date={
                                new Date(entry?.expiry).getTime() + 3600000
                              }
                              renderer={renderer}
                            />
                          </td>
                        </tr>
                      ))
                      : []}
                  </tbody>
                </table>
              </div>
            </div>
            <div>
              <div>
                <Pagination
                  totalItems={data?.data?.length}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
            {/* <div className="container">
              <div className="row">
                <div className="col-xxl">
                  <span className="counter">Showing 8 of 50</span>
                  <div className="float">
                    <span className="prev">Previous</span>
                    <span className="nxt">Next</span>
                  </div>
                </div>
              </div>
            </div> */}
          </div >
        </div >
      </div >
    </div >
  );
};

export default Tipster;
